import React, {useState} from 'react';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {nanoid} from 'nanoid';

import useQuery from '@pluto/query-hook';
import IssueCreateDialog from './IssueCreateDialog';
import {
    IssueType,
    IngestionBlockValues,
    AspectRatioValues,
    DropFrameValues,
    FrameRateValues,
    CreateIssueParams,
} from '../JiraMenu/JiraTypes';
import {QCJobItemModified, SelectChangeEvent} from '../../../QCManager/QCManagerTypes';
import {CreateMultipleIssueType} from '../../QCJobsContainer';

export type CreateIssueType = (params: CreateIssueParams) => void;

interface IssueCreateDialogContainerProps {
    createIssue?: CreateIssueType;
    createMultipleIssue?: CreateMultipleIssueType;
    items?: number[];
    dialogIsOpen: boolean;
    handleDialogClose: () => void;
    title?: string;
    priority: number;
    contentPartner?: string;
    labPartner?: string;
    id?: number;
    data?: QCJobItemModified[],
    setCheckedItems?: (items: number[]) => void;
}

interface JiraIssueCreateHandlersProps {
    id: number;
    issueType: string;
    issueSpecificValues: string[];
    assetTypesAffected: string[];
    summary: string;
    assetsNumber: number,
    description: string;
    aspectRatioValue: string;
    dropFrameValue: string;
    frameRateValue: string;
    ingestionBlockValue: string;
    offset?: string;
    limit?: string;
    createIssue: CreateIssueType;
    handleDialogClose: () => void;
    clearValuesOnClose: () => void;
}

interface JiraMultipleIssueCreateHandlersProps {
    items: number[];
    issueType: string;
    issueSpecificValues: string[];
    assetTypesAffected: string[];
    summary: string;
    assetsNumber: number,
    description: string;
    aspectRatioValue: string;
    dropFrameValue: string;
    frameRateValue: string;
    ingestionBlockValue: string;
    offset?: string;
    limit?: string;
    createMultipleIssue: CreateMultipleIssueType;
    handleDialogClose: () => void;
    setCheckedItems: (items: number[]) => void;
    clearValuesOnClose: () => void;
}

interface ClearValuesHandlersProps {
    setIssueSpecificValues: (values: string[]) => void;
}

interface ClearValuesOnCloseHandlersProps {
    setIssueType: (value: string) => void;
    setIssueSpecificValues: (values: string[]) => void;
    setAssetTypesAffected: (values: string[]) => void;
    setIngestionBlockValue: (value: string) => void;
    setSummary: (value: string) => void;
    setAssetsNumber: (value: number) => void;
    setDescription: (value: string) => void;
    setAspectRatioValue: (value: string) => void;
    setDropFrameValue: (value: string) => void;
    setFrameRateValue: (value: string) => void;
}

export default function IssueCreateDialogContainer(props: IssueCreateDialogContainerProps) {
    const {
        createIssue,
        createMultipleIssue,
        items = [],
        dialogIsOpen,
        handleDialogClose,
        title,
        priority,
        contentPartner,
        labPartner,
        id,
        data,
        setCheckedItems = () => {},
    } = props;
    
    const query = useQuery();
    const [issueType, setIssueType] = useState<string>(IssueType.Caption);
    const [issueSpecificValues, setIssueSpecificValues] = useState<string[]>([]);
    const [assetTypesAffected, setAssetTypesAffected] = useState<string[]>([]);
    const [summary, setSummary] = useState<string>('');
    const [assetsNumber, setAssetsNumber] = useState<number>(1);
    const [description, setDescription] = useState<string>('');
    const [aspectRatioValue, setAspectRatioValue] = useState<string>(AspectRatioValues.FirstValue);
    const [ingestionBlockValue, setIngestionBlockValue] = useState<string>(IngestionBlockValues.Blocked);
    const [dropFrameValue, setDropFrameValue] = useState<string>(DropFrameValues.DF);
    const [frameRateValue, setFrameRateValue] = useState<string>(FrameRateValues.FirstRate);
    const offset = query.get('offset') || '0';
    const limit = query.get('limit') || '25';
    const isMultiple = items.length > 0;
    const clearValues = onClearValues({
        setIssueSpecificValues,
    });
    
    const clearValuesOnClose = onClearValuesOnCLose({
        setIssueType,
        setIssueSpecificValues,
        setAssetTypesAffected,
        setIngestionBlockValue,
        setSummary,
        setAssetsNumber,
        setDescription,
        setAspectRatioValue,
        setDropFrameValue,
        setFrameRateValue,
    });
    const handleChangeIssueType = onHandleChangeIssueType(clearValues, setIssueType);
    const handleChangeSummary = onHandleChangeSummary(setSummary);
    const handleChangeAssetsNumber = onHandleChangeAssetsNumber(setAssetsNumber);
    const handleChangeDescription = onHandleChangeDescription(setDescription);
    const handleChangeAspectRatio = onHandleChangeAspectRatio(setAspectRatioValue);
    const handleChangeDropFrame = onHandleChangeDropFrame(setDropFrameValue);
    const handleChangeFrameRate = onHandleChangeFrameRate(setFrameRateValue);
    const handleChangeIngestionBlock = onHandleChangeIngestionBlock(setIngestionBlockValue);
    const isSaveDisabled = handleSaveDisabled(summary, description);
    
    const onSaveHandler = handleJiraIssueCreate({
        id,
        issueType,
        issueSpecificValues,
        assetTypesAffected,
        summary,
        assetsNumber,
        description,
        aspectRatioValue,
        dropFrameValue,
        frameRateValue,
        ingestionBlockValue,
        offset,
        limit,
        createIssue,
        handleDialogClose,
        clearValuesOnClose,
    });
    
    const onMultipleSaveHandler = handleJMultipleJiraIssuesCreate({
        items,
        issueType,
        issueSpecificValues,
        assetTypesAffected,
        summary,
        assetsNumber,
        description,
        aspectRatioValue,
        dropFrameValue,
        frameRateValue,
        ingestionBlockValue,
        offset,
        limit,
        createMultipleIssue,
        handleDialogClose,
        setCheckedItems,
        clearValuesOnClose,
    });
    
    return (
        <IssueCreateDialog
            dialogIsOpen={dialogIsOpen}
            handleDialogClose={handleDialogClose}
            title={title}
            contentPartner={contentPartner}
            issueType={issueType}
            handleChangeIssueType={handleChangeIssueType}
            jobPriority={priority}
            issueSpecificValues={issueSpecificValues}
            setIssueSpecificValues={setIssueSpecificValues}
            assetTypesAffected={assetTypesAffected}
            setAssetTypesAffected={setAssetTypesAffected}
            handleChangeSummary={handleChangeSummary}
            handleChangeAssetsNumber={handleChangeAssetsNumber}
            handleChangeDescription={handleChangeDescription}
            handleChangeAspectRatio={handleChangeAspectRatio}
            aspectRatioValue={aspectRatioValue}
            handleChangeDropFrame={handleChangeDropFrame}
            dropFrameValue={dropFrameValue}
            handleChangeFrameRate={handleChangeFrameRate}
            frameRateValue={frameRateValue}
            handleChangeIngestionBlock={handleChangeIngestionBlock}
            ingestionBlockValue={ingestionBlockValue}
            clearValuesOnClose={clearValuesOnClose}
            onSaveHandler={onSaveHandler}
            onMultipleSaveHandler={onMultipleSaveHandler}
            isSaveDisabled={isSaveDisabled}
            isMultiple={isMultiple}
            data={data}
            checkedItems={items}
            labPartner={labPartner}
        />
    );
}

export const mapContentPartners = curry(((name) => {
    return <li key={nanoid()}>{`- ${name}`}</li>;
}));

export const getSelectedContentPartnersNames = (data: QCJobItemModified[], checkedItems: number[]) => {
    const selectedContentPartnersNamesArr = data.filter((item) => checkedItems.includes(item.id)).map((job) => job.contentPartner);
    
    return (
        <ul className="jobs-names-list">
            {selectedContentPartnersNamesArr.map(mapContentPartners)}
        </ul>
    );
};

export const getLabPartnerName = (data: QCJobItemModified[], jobPriority: number) => {
    const itemWithHighestPrio = getHighestPriorityItem(data, jobPriority);
    const {contentItem} = itemWithHighestPrio[0] || [];
    
    return contentItem?.labPartner;
};

const getHighestPriorityItem = (data: QCJobItemModified[], jobPriority: number) => {
    return data.filter((item: QCJobItemModified) => item.priority === jobPriority);
};

export const handleSaveDisabled = (summary: string, description: string): boolean => {
    return !summary.trim().length || !description.trim().length;
};

export const getSpecificValues = (issueType: string, issueSpecificValues: string[], specificIssueType: string): string[] => {
    return issueType === specificIssueType ? issueSpecificValues : [];
};

export const handleJiraIssueCreate = wrap((handlers: JiraIssueCreateHandlersProps) => {
    const {
        id,
        issueType,
        issueSpecificValues,
        assetTypesAffected,
        summary,
        assetsNumber,
        description,
        aspectRatioValue,
        dropFrameValue,
        frameRateValue,
        ingestionBlockValue,
        offset,
        limit,
        createIssue,
        handleDialogClose,
        clearValuesOnClose,
    } = handlers;
    const body = {
        summary,
        description,
        assetsAffectedNumber: assetsNumber,
        issuetype: issueType,
        dropFrame: dropFrameValue,
        frameRate: frameRateValue,
        aspectRatio: aspectRatioValue,
        issueDetails: getSpecificValues(issueType, issueSpecificValues, 'Caption'),
        typesAffected: getSpecificValues(issueType, issueSpecificValues, 'Art'),
        abIssueDetail: getSpecificValues(issueType, issueSpecificValues, 'Ad-break'),
        misdeliveryDetails: getSpecificValues(issueType, issueSpecificValues, 'Misdelivery'),
        affectedAssetTypes: assetTypesAffected,
        ingestionBlock: ingestionBlockValue,
    };
    const params = {
        id,
        body,
        offset,
        limit,
    };
    
    createIssue(params);
    clearValuesOnClose();
    handleDialogClose();
});

export const handleJMultipleJiraIssuesCreate = wrap((handlers: JiraMultipleIssueCreateHandlersProps) => {
    const {
        items,
        issueType,
        issueSpecificValues,
        assetTypesAffected,
        summary,
        assetsNumber,
        description,
        aspectRatioValue,
        dropFrameValue,
        frameRateValue,
        ingestionBlockValue,
        offset,
        limit,
        createMultipleIssue,
        handleDialogClose,
        setCheckedItems,
        clearValuesOnClose,
    } = handlers;
    const body = {
        qcJobIds: items,
        summary,
        description,
        assetsAffectedNumber: assetsNumber,
        issuetype: issueType,
        dropFrame: dropFrameValue,
        frameRate: frameRateValue,
        aspectRatio: aspectRatioValue,
        issueDetails: getSpecificValues(issueType, issueSpecificValues, 'Caption'),
        typesAffected: getSpecificValues(issueType, issueSpecificValues, 'Art'),
        abIssueDetail: getSpecificValues(issueType, issueSpecificValues, 'Ad-break'),
        misdeliveryDetails: getSpecificValues(issueType, issueSpecificValues, 'Misdelivery'),
        affectedAssetTypes: assetTypesAffected,
        ingestionBlock: ingestionBlockValue,
    };
    
    const params = {
        body,
        offset,
        limit,
    };
    
    createMultipleIssue(params);
    clearValuesOnClose();
    setCheckedItems([]);
    handleDialogClose();
});

export const onHandleChangeIngestionBlock = curry((setIngestingBlockValue: (value: string) => void, e: SelectChangeEvent) => {
    setIngestingBlockValue(e.target.value);
});

export const onHandleChangeFrameRate = curry((setFrameRateValue: (value: string) => void, e: SelectChangeEvent) => {
    setFrameRateValue(e.target.value);
});

export const onHandleChangeDropFrame = curry((setDropFrameValue: (value: string) => void, e: SelectChangeEvent) => {
    setDropFrameValue(e.target.value);
});

export const onHandleChangeAspectRatio = curry((setAspectRatioValue: (value: string) => void, e: SelectChangeEvent) => {
    setAspectRatioValue(e.target.value);
});

export const onHandleChangeIssueType = curry((clearValues: () => void, setIssueType: (value: string) => void, e: SelectChangeEvent) => {
    clearValues();
    setIssueType(e.target.value);
});

export const onHandleChangeSummary = curry((setSummary: (value: string) => void, e: SelectChangeEvent) => {
    setSummary(e.target.value);
});

export const onHandleChangeAssetsNumber = curry((setAssetsNumber: (value: number) => void, e: SelectChangeEvent) => {
    setAssetsNumber(Number(e.target.value));
});

export const onHandleChangeDescription = curry((setDescription: (value: string) => void, e: SelectChangeEvent) => {
    setDescription(e.target.value);
});

export const onClearValues = wrap((clearValuesHandlers: ClearValuesHandlersProps) => {
    const {setIssueSpecificValues} = clearValuesHandlers;
    setIssueSpecificValues([]);
});

export const onClearValuesOnCLose = wrap((clearValuesOnCloseHandlers: ClearValuesOnCloseHandlersProps) => {
    const {
        setIssueType,
        setIssueSpecificValues,
        setAssetTypesAffected,
        setIngestionBlockValue,
        setSummary,
        setAssetsNumber,
        setDescription,
        setAspectRatioValue,
        setDropFrameValue,
        setFrameRateValue,
    } = clearValuesOnCloseHandlers;
    setIssueSpecificValues([]);
    setAssetTypesAffected([]);
    setIssueType(IssueType.Caption);
    setIngestionBlockValue(IngestionBlockValues.Blocked);
    setSummary('');
    setAssetsNumber(1);
    setDescription('');
    setAspectRatioValue(AspectRatioValues.FirstValue);
    setDropFrameValue(DropFrameValues.DF);
    setFrameRateValue(FrameRateValues.FirstRate);
});

IssueCreateDialogContainer.defaultProps = {
    title: '',
    items: [],
    id: 1,
    data: [],
    contentPartner: '',
    labPartner: '',
    createIssue: () => {},
    createMultipleIssue: () => {},
    setCheckedItems: () => {},
};
